import { CommonModule } from '@angular/common';
import { Component, inject, Inject, OnDestroy, OnInit, Optional, signal, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { Subject, takeUntil } from 'rxjs';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { SettingsService } from '@app/_services/settings.service';
import { CacheService } from '@app/_services/cache.service';
import { BRAND_LOGO } from '@app/_utils/brand-icons';
import { AlertService } from '@app/_services/alert.service';
import { ConsolidatedApiService } from '@app/_services/consolidated-api.service';
import { IndexDbService } from '@app/_services/index-db.service';
import { CustomBottomSheetRef } from '@app/_services/custom-bottom-sheet.service';
import { log } from '@app/_services/logger';

/** Shape of an AI memory fact as stored in Firestore */
export interface AiMemoryFact {
  id: string;
  fact: string;
  category: string;
  createdAt: Date;
}

@Component({
  selector: 'app-meo-ai-memory',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TextFieldModule,
    SvgComponent,
  ],
  templateUrl: './meo-ai-memory.component.html',
  styleUrl: './meo-ai-memory.component.scss',
})
export class MeoAiMemoryComponent implements OnInit, OnDestroy {
  readonly ss = inject(SettingsService);
  readonly cc = inject(CacheService);
  readonly brandLogo = BRAND_LOGO;
  private readonly alertService = inject(AlertService);
  private readonly consolidatedApi = inject(ConsolidatedApiService);
  private readonly idbService = inject(IndexDbService);

  /** All facts from IDB user data, sorted newest first */
  facts: WritableSignal<AiMemoryFact[]> = signal([]);

  /** Loading state */
  isLoading: WritableSignal<boolean> = signal(true);

  /** Inline edit state */
  editingFactId: string | null = null;
  editText = '';

  /** Add new memory state */
  isAdding = false;
  addText = '';

  /** Unsubscribe subject */
  private unSubscribe = new Subject<void>();

  constructor(
    @Optional() public dialogRef: MatDialogRef<MeoAiMemoryComponent> | null,
    @Optional() public bottomSheetRef: CustomBottomSheetRef<MeoAiMemoryComponent> | null,
    @Optional() @Inject(MAT_DIALOG_DATA) dialogData: unknown | null,
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) bottomSheetData: unknown | null,
  ) {
    dialogRef?.backdropClick().subscribe(() => {
      this.close();
    });

    bottomSheetRef?.backdropClick().subscribe(() => {
      this.close();
    });
  }

  ngOnInit(): void {
    this.subscribeToUserData();
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }

  /**
   * Subscribe to user data from IDB for real-time aiMemory updates via RTDB sync.
   */
  private subscribeToUserData(): void {
    this.idbService.user$.pipe(takeUntil(this.unSubscribe)).subscribe((data) => {
      if (data && data.length > 0) {
        const userData = data[0] as unknown as Record<string, unknown>;
        const aiMemory = userData['aiMemory'] as Record<string, unknown> | undefined;
        const rawFacts = (aiMemory?.['facts'] as Record<string, unknown>[]) || [];
        const facts: AiMemoryFact[] = rawFacts.map((f) => ({
          id: f['id'] as string,
          fact: f['fact'] as string,
          category: f['category'] as string,
          createdAt: f['createdAt'] instanceof Date
            ? f['createdAt']
            : (f['createdAt'] as { toDate?: () => Date })?.toDate?.() || new Date(),
        }));
        // Sort newest first
        facts.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        this.facts.set(facts);
      } else {
        this.facts.set([]);
      }
      this.isLoading.set(false);
    });
  }

  /**
   * Delete a fact from the user's aiMemory via v2-api.
   */
  async deleteFact(fact: AiMemoryFact): Promise<void> {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('meo_ai_preferences_delete_confirm'),
      description: fact.fact,
      yesButtonText: this.ss.t('common_delete'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-11',
      noButtonColor: 'color-7',
    });

    if (res !== true) return;

    // Optimistic update
    const previousFacts = this.facts();
    this.facts.set(previousFacts.filter(f => f.id !== fact.id));

    try {
      const result = await this.consolidatedApi.call('aiMemory_deleteFact', { factId: fact.id });
      if (result.error) {
        throw new Error(result.error);
      }
      log.info('MeoAiMemory', 'Fact deleted', { factId: fact.id });
    } catch (error) {
      this.facts.set(previousFacts);
      log.error('MeoAiMemory', 'Error deleting fact', error as Error);
    }
  }

  // ==================== Inline Edit ====================

  startEdit(fact: AiMemoryFact): void {
    this.editingFactId = fact.id;
    this.editText = fact.fact;
  }

  cancelEdit(): void {
    this.editingFactId = null;
    this.editText = '';
  }

  onContentClick(event: MouseEvent): void {
    // Dismiss edit mode when clicking empty space (not on a fact-item or action button)
    const target = event.target as HTMLElement;
    if (!target.closest('.fact-item') && !target.closest('.action-btn')) {
      this.cancelEdit();
    }
  }

  onEditBlur(fact: AiMemoryFact): void {
    // Delay to allow mousedown on action buttons to fire first
    setTimeout(() => {
      if (this.editingFactId === fact.id) {
        this.cancelEdit();
      }
    }, 150);
  }

  async saveEdit(fact: AiMemoryFact): Promise<void> {
    const trimmed = this.editText.trim();
    if (!trimmed || trimmed === fact.fact) {
      this.cancelEdit();
      return;
    }

    // Optimistic update
    const previousFacts = this.facts();
    const updatedFacts = previousFacts.map(f =>
      f.id === fact.id ? { ...f, fact: trimmed } : f,
    );
    this.facts.set(updatedFacts);
    this.cancelEdit();

    try {
      const result = await this.consolidatedApi.call('aiMemory_editFact', {
        factId: fact.id,
        fact: trimmed,
        category: fact.category,
      });
      if (result.error) {
        throw new Error(result.error);
      }
      log.info('MeoAiMemory', 'Fact edited', { factId: fact.id });
    } catch (error) {
      this.facts.set(previousFacts);
      log.error('MeoAiMemory', 'Error editing fact', error as Error);
    }
  }

  onEditKeydown(event: KeyboardEvent, fact: AiMemoryFact): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveEdit(fact);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation(); // Prevent CDK from closing the dialog
      this.cancelEdit();
    }
  }

  // ==================== Add New Memory ====================

  startAdd(): void {
    this.isAdding = true;
    this.addText = '';
  }

  cancelAdd(): void {
    this.isAdding = false;
    this.addText = '';
  }

  async saveAdd(): Promise<void> {
    const trimmed = this.addText.trim();
    if (!trimmed) {
      this.cancelAdd();
      return;
    }

    const newFact: AiMemoryFact = {
      id: `pref_${Date.now()}`,
      fact: trimmed,
      category: 'general',
      createdAt: new Date(),
    };

    // Optimistic update
    const previousFacts = this.facts();
    const updatedFacts = [newFact, ...previousFacts];
    this.facts.set(updatedFacts);
    this.cancelAdd();

    try {
      const result = await this.consolidatedApi.call('aiMemory_addFact', {
        fact: trimmed,
        category: 'general',
      });
      if (result.error) {
        throw new Error(result.error);
      }
      log.info('MeoAiMemory', 'Fact added', { factId: newFact.id });
    } catch (error) {
      this.facts.set(previousFacts);
      log.error('MeoAiMemory', 'Error adding fact', error as Error);
    }
  }

  onAddKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveAdd();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation(); // Prevent CDK from closing the dialog
      this.cancelAdd();
    }
  }

  /**
   * Close the dialog or bottom sheet.
   */
  close(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss();
    }
  }
}
